import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AppDispatch } from 'store'
import { Input } from '../../components/Input/Input'
import { NavBar } from '../../components/NavBar/NavBar'
import { addCompany } from '../../features/companies/companiesSlice'
import {
  Flex,
  ItemsGrid,
  Button,
  Separator,
  H1WithBack,
  PageWrapper,
  PageFooter,
} from '../../styling/GlobalStyles'
import { ICompany } from 'types/companies.interface'
import { getIndustries } from 'features/industries/industriesSlice'
import { CancelBtn } from 'components/Button/CancelBtn'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { ISelectValue } from 'constants/globalTypes'
import { companiesInitialState, companyInputs } from './companiesData'
import { minimumCharacters, standardFieldValidation, validateSelect } from 'utils/validationUtils'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { ToggleButton } from 'components/Input/ToggleButton'

export const AddCompany = () => {
  // states
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const [invalidFields, setInvalidFields] = useState(companyInputs)
  const [inputValues, setInputValues] = useState<ICompany>(companiesInitialState)

  useFocusFirstInput()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!inputValues?.countryCode) {
      setInvalidFields((prev) => (prev.includes('countryCode') ? prev : [...prev, 'countryCode']))
      toast.warn('Country must be selected')
      return
    }
    if (!inputValues?.settings?.currency) {
      setInvalidFields((prev) => (prev.includes('currency') ? prev : [...prev, 'currency']))
      toast.warn('Currency must be selected')
      return
    }
    if (invalidFields.length > 0) {
      toast.warn('All fields are required')
      return
    }

    const resp = await dispatch(addCompany(inputValues))
    if (resp.meta.requestStatus === 'fulfilled') {
      navigate('/companies')
    }
  }

  const handleSelect = (value: ISelectValue, name: string) => {
    validateSelect(value, name, setInvalidFields)
    if (name == 'currency') {
      setInputValues({ ...inputValues, settings: { ...inputValues.settings, currency: String(value.value) } })
    } else {
      setInputValues({ ...inputValues, [name]: value.value })
    }
  }

  useEffect(() => {
    dispatch(getIndustries(null))
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name.split('-')
    const value = e.target.value
    if (name[1] == 'username') {
      minimumCharacters(value, 'representative-username', setInvalidFields, 6)
    } else {
      standardFieldValidation(e, setInvalidFields)
    }
    const copyState = { ...inputValues }
    setInputValues(copyState)

    if (name[0] === 'main') {
      setInputValues((prev) => {
        prev[name[1]] = value
        return prev
      })
    } else if (name[0] === 'contact') {
      setInputValues((prev) => {
        prev.contact[name[1]] = value
        return prev
      })
    }
  }

  const parseIndustries = (data: any) => {
    return data.data.map((item: any) => ({ label: item.name, value: item.id }))
  }

  return (
    <>
      <NavBar />
      <PageWrapper>
        <H1WithBack title={'Add Company'} arrow />
        <ItemsGrid columns={2} width='80%'>
          <Input
            nomarg
            required
            type='text'
            name='main-name'
            labelText='Company name'
            placeholder='Enter company name'
            value={inputValues.name}
            onChange={handleChange}
            invalid={invalidFields.includes('main-name') ? 'true' : ''}
          />

          <HonestSelectAsyncInput
            nomarg
            required
            link='industries'
            placeholder='Search industries'
            name='industryId'
            labelText='Industry'
            defaultValue={inputValues.industry.name}
            onChange={(type: ISelectValue, name: string) => handleSelect(type, name)}
            parserFunction={parseIndustries}
            invalid={invalidFields.includes('industryId')}
          />
          <Input
            nomarg
            required
            name='contact-address'
            type='text'
            labelText='Company address'
            placeholder='Enter company address'
            value={inputValues.contact.address}
            onChange={handleChange}
            invalid={invalidFields.includes('contact-address') ? 'true' : ''}
          />

          <Input
            nomarg
            required
            name='contact-phoneNumber'
            type='tel'
            labelText='Company phone number'
            placeholder='Enter company phone number'
            value={inputValues.contact.phoneNumber}
            onChange={handleChange}
            invalid={invalidFields.includes('contact-phoneNumber') ? 'true' : ''}
          />

          <Input
            nomarg
            required
            name='contact-email'
            type='email'
            labelText='Company email'
            placeholder='Enter company email'
            value={inputValues.contact.email}
            onChange={handleChange}
            invalid={invalidFields.includes('contact-email') ? 'true' : ''}
          />

          <HonestSelectAsyncInput required onChange={handleSelect} fixedList={countries} labelText='Country' name='countryCode' invalid={invalidFields.includes('countryCode')} />
          <HonestSelectAsyncInput required onChange={handleSelect} fixedList={currencies} labelText='Currency' name='currency' invalid={invalidFields.includes('currency')} />
          <Input
            nomarg
            required
            type='number'
            labelText='Company VAT'
            name='main-taxNumber'
            placeholder='Enter company VAT'
            value={inputValues.taxNumber}
            onChange={handleChange}
            invalid={invalidFields.includes('main-taxNumber') ? 'true' : ''}
          />
          <ToggleButton
            state={inputValues.settings?.shouldIncludeTraffic || false}
            handleChange={() =>
              setInputValues((prev: ICompany) => ({
                ...prev,
                settings: {
                  ...prev.settings,
                  shouldIncludeTraffic: !prev.settings.shouldIncludeTraffic
                }

              }))
            }
            maxWidth
            label='Should Include Traffic'
          />
        </ItemsGrid>
      </PageWrapper>
      <PageFooter>
        <Flex gap='1rem'>
          <CancelBtn />
          <Button orange onClick={handleSubmit}>
            Save
          </Button>
        </Flex>
      </PageFooter>
    </>
  )
}

export const countries = [
  { value: 'AF', label: 'Afghanistan' },
  { value: 'AL', label: 'Albania' },
  { value: 'DZ', label: 'Algeria' },
  { value: 'AO', label: 'Angola' },
  { value: 'AR', label: 'Argentina' },
  { value: 'AM', label: 'Armenia' },
  { value: 'AU', label: 'Australia' },
  { value: 'AT', label: 'Austria' },
  { value: 'AZ', label: 'Azerbaijan' },

  // Africa
  { value: 'BJ', label: 'Benin' },
  { value: 'BW', label: 'Botswana' },
  { value: 'BF', label: 'Burkina Faso' },
  { value: 'BI', label: 'Burundi' },
  { value: 'CM', label: 'Cameroon' },
  { value: 'CV', label: 'Cape Verde' },
  { value: 'CF', label: 'Central African Republic' },
  { value: 'TD', label: 'Chad' },
  { value: 'CD', label: 'Democratic Republic of the Congo' },
  { value: 'DJ', label: 'Djibouti' },
  { value: 'EG', label: 'Egypt' },
  { value: 'GQ', label: 'Equatorial Guinea' },
  { value: 'ER', label: 'Eritrea' },
  { value: 'ET', label: 'Ethiopia' },
  { value: 'GA', label: 'Gabon' },
  { value: 'GM', label: 'Gambia' },
  { value: 'GH', label: 'Ghana' },
  { value: 'GN', label: 'Guinea' },
  { value: 'GW', label: 'Guinea-Bissau' },
  { value: 'CI', label: 'Ivory Coast' },
  { value: 'KE', label: 'Kenya' },
  { value: 'LS', label: 'Lesotho' },
  { value: 'LR', label: 'Liberia' },
  { value: 'LY', label: 'Libya' },
  { value: 'MG', label: 'Madagascar' },
  { value: 'MW', label: 'Malawi' },
  { value: 'ML', label: 'Mali' },
  { value: 'MR', label: 'Mauritania' },
  { value: 'MU', label: 'Mauritius' },
  { value: 'MA', label: 'Morocco' },
  { value: 'MZ', label: 'Mozambique' },
  { value: 'NA', label: 'Namibia' },
  { value: 'NE', label: 'Niger' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'RW', label: 'Rwanda' },
  { value: 'SN', label: 'Senegal' },
  { value: 'SL', label: 'Sierra Leone' },
  { value: 'SO', label: 'Somalia' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'SS', label: 'South Sudan' },
  { value: 'SD', label: 'Sudan' },
  { value: 'TZ', label: 'Tanzania' },
  { value: 'TG', label: 'Togo' },
  { value: 'TN', label: 'Tunisia' },
  { value: 'UG', label: 'Uganda' },
  { value: 'ZM', label: 'Zambia' },
  { value: 'ZW', label: 'Zimbabwe' },

  // Other regions
  { value: 'BD', label: 'Bangladesh' },
  { value: 'BE', label: 'Belgium' },
  { value: 'BR', label: 'Brazil' },
  { value: 'BG', label: 'Bulgaria' },
  { value: 'CA', label: 'Canada' },
  { value: 'CL', label: 'Chile' },
  { value: 'CN', label: 'China' },
  { value: 'HR', label: 'Croatia' },
  { value: 'CZ', label: 'Czech Republic' },
  { value: 'DK', label: 'Denmark' },
  { value: 'FI', label: 'Finland' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'GR', label: 'Greece' },
  { value: 'HU', label: 'Hungary' },
  { value: 'IN', label: 'India' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'IR', label: 'Iran' },
  { value: 'IQ', label: 'Iraq' },
  { value: 'IE', label: 'Ireland' },
  { value: 'IL', label: 'Israel' },
  { value: 'IT', label: 'Italy' },
  { value: 'JP', label: 'Japan' },
  { value: 'MX', label: 'Mexico' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'NO', label: 'Norway' },
  { value: 'PL', label: 'Poland' },
  { value: 'PT', label: 'Portugal' },
  { value: 'RO', label: 'Romania' },
  { value: 'RU', label: 'Russia' },
  { value: 'RS', label: 'Serbia' },
  { value: 'ES', label: 'Spain' },
  { value: 'SE', label: 'Sweden' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'TR', label: 'Turkey' },
  { value: 'UA', label: 'Ukraine' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' }
];

export const currencies = [
  { value: 'USD', label: 'US Dollar' },
  { value: 'EUR', label: 'Euro' },
  { value: 'GBP', label: 'British Pound' },
  { value: 'RSD', label: 'Serbian Dinar' },
  { value: 'CHF', label: 'Swiss Franc' },
  { value: 'AUD', label: 'Australian Dollar' },
  { value: 'CAD', label: 'Canadian Dollar' },
  { value: 'NZD', label: 'New Zealand Dollar' },
  { value: 'JPY', label: 'Japanese Yen' },
  { value: 'CNY', label: 'Chinese Yuan' },
  { value: 'INR', label: 'Indian Rupee' },
  { value: 'RUB', label: 'Russian Ruble' },
  { value: 'TRY', label: 'Turkish Lira' },
  { value: 'AED', label: 'UAE Dirham' },
  { value: 'SAR', label: 'Saudi Riyal' },
  { value: 'QAR', label: 'Qatari Riyal' },
  { value: 'KWD', label: 'Kuwaiti Dinar' },
  { value: 'EGP', label: 'Egyptian Pound' },
  { value: 'ZAR', label: 'South African Rand' },
  { value: 'NGN', label: 'Nigerian Naira' },
  { value: 'KES', label: 'Kenyan Shilling' },
  { value: 'GHS', label: 'Ghanaian Cedi' },
  { value: 'MAD', label: 'Moroccan Dirham' },
  { value: 'TND', label: 'Tunisian Dinar' },
  { value: 'DZD', label: 'Algerian Dinar' },
  { value: 'ETB', label: 'Ethiopian Birr' },
  { value: 'UGX', label: 'Ugandan Shilling' },
  { value: 'TZS', label: 'Tanzanian Shilling' },
  { value: 'BRL', label: 'Brazilian Real' },
  { value: 'MXN', label: 'Mexican Peso' },
  { value: 'ARS', label: 'Argentine Peso' },
  { value: 'CLP', label: 'Chilean Peso' },
  { value: 'COP', label: 'Colombian Peso' },
  { value: 'PEN', label: 'Peruvian Sol' },
  { value: 'KRW', label: 'South Korean Won' },
  { value: 'SGD', label: 'Singapore Dollar' },
  { value: 'HKD', label: 'Hong Kong Dollar' },
  { value: 'THB', label: 'Thai Baht' },
  { value: 'MYR', label: 'Malaysian Ringgit' },
  { value: 'IDR', label: 'Indonesian Rupiah' },
  { value: 'PHP', label: 'Philippine Peso' },
  { value: 'VND', label: 'Vietnamese Dong' },
  { value: 'PKR', label: 'Pakistani Rupee' },
  { value: 'BDT', label: 'Bangladeshi Taka' },
  { value: 'LKR', label: 'Sri Lankan Rupee' },
  { value: 'NOK', label: 'Norwegian Krone' },
  { value: 'SEK', label: 'Swedish Krona' },
  { value: 'DKK', label: 'Danish Krone' },
  { value: 'PLN', label: 'Polish Zloty' },
  { value: 'CZK', label: 'Czech Koruna' },
  { value: 'HUF', label: 'Hungarian Forint' },
  { value: 'RON', label: 'Romanian Leu' },
  { value: 'BGN', label: 'Bulgarian Lev' },
  { value: 'HRK', label: 'Croatian Kuna' }
];